import { useState } from 'react';

import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import AudiotrackRoundedIcon from '@mui/icons-material/AudiotrackRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EmojiEmotionsRoundedIcon from '@mui/icons-material/EmojiEmotionsRounded';
import GifBoxRoundedIcon from '@mui/icons-material/GifBoxRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getIntlLocale } from '@/shared/i18n';

import { formatBytes, formatMediaDuration } from './format';
import type {
  ConversationAttachmentStorageStatusLike,
  ConversationAttachmentTypeLike,
} from './types';

const TYPE_ICON: Record<ConversationAttachmentTypeLike, typeof ImageRoundedIcon> = {
  IMAGE: ImageRoundedIcon,
  DOCUMENT: InsertDriveFileRoundedIcon,
  AUDIO: AudiotrackRoundedIcon,
  VOICE: MicRoundedIcon,
  VIDEO: VideocamRoundedIcon,
  VIDEO_NOTE: VideocamRoundedIcon,
  ANIMATION: GifBoxRoundedIcon,
  STICKER: EmojiEmotionsRoundedIcon,
  UNKNOWN: AttachFileRoundedIcon,
};

export interface AttachmentTileProps {
  type: ConversationAttachmentTypeLike;
  mimeType?: string | null;
  sizeBytes?: number | null;
  fileName?: string | null;
  durationSeconds?: number | null;
  storageStatus: ConversationAttachmentStorageStatusLike;
  /**
   * The BFF content URL already resolved to an absolute address by the caller (via
   * `resolveAttachmentContentUrl` in `features/conversations`) — `shared/ui` never imports a feature's
   * API layer, so it never resolves this itself. `null` whenever `storageStatus !== 'STORED'`.
   */
  resolvedUrl: string | null;
  /**
   * `true` when this attachment's type needs `customers.feature.pii-view` and the caller lacks it —
   * precomputed by the caller (design §11.3's permission matrix, `PII_GATED_ATTACHMENT_TYPES` + the
   * conservative `UNKNOWN` treatment of contract gap G15).
   */
  locked: boolean;
  /** Customer 360's "type chip only, no media, no bytes fetched" list mode (design §11.3). */
  compact?: boolean;
}

function PlaceholderChip({
  type,
  storageStatus,
}: {
  type: ConversationAttachmentTypeLike;
  storageStatus: ConversationAttachmentStorageStatusLike;
}) {
  const { t } = useTranslation('conversations');
  const Icon = TYPE_ICON[type] ?? AttachFileRoundedIcon;
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<Icon fontSize="inherit" />}
      label={`${t(`attachments.types.${type}`, type)} · ${t(`attachments.storage.${storageStatus}`, storageStatus)}`}
      title={t(`attachments.storageTooltip.${storageStatus}`, '')}
      sx={{ color: 'text.secondary' }}
    />
  );
}

function LockedChip({ type }: { type: ConversationAttachmentTypeLike }) {
  const { t } = useTranslation('conversations');
  const typeLabel = t(`attachments.types.${type}`, type);
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<LockOutlinedIcon fontSize="inherit" />}
      label={t('attachments.locked', { type: typeLabel })}
      title={t('attachments.lockedTooltip')}
      aria-disabled
    />
  );
}

/**
 * A genuine load failure (a `STORED` file whose bytes 404/500 — not the same thing as never having
 * been stored). Distinct from {@link PlaceholderChip}: that one's `storageStatus` labels ("could not
 * be stored", "removed after the retention period", …) all describe the FILE, not a transient network
 * failure of an otherwise-good one, and would misinform an operator into thinking the customer's
 * upload never made it.
 */
function ImageLoadErrorChip({ type }: { type: ConversationAttachmentTypeLike }) {
  const { t } = useTranslation('conversations');
  const Icon = TYPE_ICON[type] ?? AttachFileRoundedIcon;
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<Icon fontSize="inherit" />}
      label={`${t(`attachments.types.${type}`, type)} · ${t('attachments.loadError')}`}
      sx={{ color: 'text.secondary' }}
    />
  );
}

function ImageLikeTile({
  resolvedUrl,
  fileName,
  compact,
  onOpen,
  onError,
}: {
  resolvedUrl: string;
  fileName?: string | null;
  compact?: boolean;
  onOpen: () => void;
  /** Reports a genuine `<img>` load failure up so the caller can swap to {@link ImageLoadErrorChip}
   * instead of leaving a broken image icon on screen. */
  onError?: () => void;
}) {
  const { t } = useTranslation('conversations');
  const [loaded, setLoaded] = useState(false);
  const size = compact ? 64 : 240;
  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      sx={{
        border: 'none',
        p: 0,
        cursor: 'pointer',
        background: 'none',
        display: 'block',
        position: 'relative',
        width: size,
        height: loaded ? 'auto' : compact ? size : 160,
      }}
    >
      {!loaded ? (
        <Skeleton
          variant="rectangular"
          width={size}
          height={compact ? size : 160}
          sx={{ borderRadius: 1, position: compact ? 'static' : 'absolute' }}
        />
      ) : null}
      <Box
        component="img"
        src={resolvedUrl}
        loading="lazy"
        alt={fileName ?? t('attachments.imageAlt')}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setLoaded(true);
          onError?.();
        }}
        sx={{
          maxWidth: size,
          maxHeight: size,
          borderRadius: 1,
          display: loaded ? 'block' : 'none',
        }}
      />
    </Box>
  );
}

/**
 * One tile per attachment, rendered by type × storage status × permission (design §11.3): STORED and
 * permitted media renders inline; STORED-but-pii-gated-without-view is a locked chip; anything not
 * `STORED` is a labelled placeholder chip. Never a broken `<img>`.
 */
export function AttachmentTile({
  type,
  mimeType,
  sizeBytes,
  fileName,
  durationSeconds,
  storageStatus,
  resolvedUrl,
  locked,
  compact = false,
}: AttachmentTileProps) {
  const { t } = useTranslation('conversations');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageErrored, setImageErrored] = useState(false);
  const locale = getIntlLocale();

  if (storageStatus !== 'STORED' || !resolvedUrl) {
    return <PlaceholderChip type={type} storageStatus={storageStatus} />;
  }
  if (locked) {
    return <LockedChip type={type} />;
  }
  if (compact) {
    // Customer 360's compact list: the type chip only — no media element, no bytes fetched.
    const Icon = TYPE_ICON[type] ?? AttachFileRoundedIcon;
    return (
      <Chip
        size="small"
        variant="outlined"
        icon={<Icon fontSize="inherit" />}
        label={t(`attachments.types.${type}`, type)}
      />
    );
  }

  if (imageErrored) {
    return <ImageLoadErrorChip type={type} />;
  }

  switch (type) {
    case 'IMAGE':
      return (
        <>
          <ImageLikeTile
            resolvedUrl={resolvedUrl}
            fileName={fileName}
            onOpen={() => setLightboxOpen(true)}
            onError={() => setImageErrored(true)}
          />
          <Dialog
            open={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            maxWidth="md"
            aria-label={t('attachments.preview')}
          >
            <DialogContent>
              <Box
                component="img"
                src={resolvedUrl}
                alt={fileName ?? t('attachments.imageAlt')}
                sx={{
                  maxHeight: '80vh',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  mx: 'auto',
                }}
              />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {fileName ?? t('attachments.types.IMAGE')}
                {sizeBytes ? ` · ${formatBytes(sizeBytes, locale)}` : ''}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button component="a" href={resolvedUrl} target="_blank" rel="noopener">
                  {t('attachments.openOriginal')}
                </Button>
                <Button onClick={() => setLightboxOpen(false)}>{t('common:action.close')}</Button>
              </Stack>
            </DialogActions>
          </Dialog>
        </>
      );

    case 'ANIMATION':
      if (mimeType?.startsWith('video/')) {
        return (
          <Box
            component="video"
            src={resolvedUrl}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-label={t('attachments.animationAria')}
            onError={() => setImageErrored(true)}
            sx={{ maxWidth: 240, maxHeight: 240, borderRadius: 1, display: 'block' }}
          />
        );
      }
      return (
        <ImageLikeTile
          resolvedUrl={resolvedUrl}
          fileName={fileName}
          onOpen={() => setLightboxOpen(true)}
          onError={() => setImageErrored(true)}
        />
      );

    case 'STICKER':
      if (mimeType?.startsWith('image/')) {
        return (
          <Box
            component="img"
            src={resolvedUrl}
            alt={t('attachments.types.STICKER')}
            onError={() => setImageErrored(true)}
            sx={{ width: 128, height: 128, display: 'block' }}
          />
        );
      }
      return (
        <Chip
          size="small"
          variant="outlined"
          icon={<EmojiEmotionsRoundedIcon fontSize="inherit" />}
          label={t('attachments.types.STICKER')}
          component="a"
          href={resolvedUrl}
          target="_blank"
          rel="noopener"
          clickable
        />
      );

    case 'AUDIO':
    case 'VOICE':
      return (
        <Stack spacing={0.25} sx={{ maxWidth: 320 }}>
          <Box
            component="audio"
            controls
            preload="none"
            src={resolvedUrl}
            aria-label={fileName ?? t(`attachments.types.${type}`)}
            sx={{ width: '100%' }}
          />
          <Typography variant="caption" color="text.secondary">
            {type === 'VOICE'
              ? t('attachments.voiceCaption', {
                  duration: formatMediaDuration(durationSeconds ?? 0),
                })
              : (fileName ?? t('attachments.types.AUDIO'))}
          </Typography>
        </Stack>
      );

    case 'VIDEO':
    case 'VIDEO_NOTE':
      return (
        <Stack spacing={0.25} sx={{ maxWidth: type === 'VIDEO_NOTE' ? 240 : 320 }}>
          <Box
            component="video"
            controls
            preload="metadata"
            src={resolvedUrl}
            onError={() => setImageErrored(true)}
            sx={{
              maxWidth: type === 'VIDEO_NOTE' ? 240 : 320,
              width: '100%',
              borderRadius: type === 'VIDEO_NOTE' ? 2 : 1,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {type === 'VIDEO_NOTE'
              ? t('attachments.videoNoteCaption')
              : (fileName ?? t('attachments.types.VIDEO'))}
          </Typography>
        </Stack>
      );

    case 'DOCUMENT':
    case 'UNKNOWN':
    default:
      return (
        <Paper variant="outlined" sx={{ p: 1, maxWidth: 320 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InsertDriveFileRoundedIcon color="action" />
            <Stack sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" noWrap>
                {fileName ?? t(`attachments.types.${type}`, t('attachments.types.DOCUMENT'))}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {[mimeType, sizeBytes ? formatBytes(sizeBytes, locale) : null]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Stack>
            <Button
              size="small"
              component="a"
              href={resolvedUrl}
              target="_blank"
              rel="noopener"
              startIcon={<DownloadRoundedIcon />}
            >
              {t('attachments.download')}
            </Button>
          </Stack>
        </Paper>
      );
  }
}
